/**
 * Binary search utilities ported from the legacy Angular `ArrayUtils`.
 * Used by the video replay tool to map a playback timestamp onto the nearest
 * GPS / heart-rate / match-event point.
 */

export type Comparator<T, V> = (element: T, searchValue: V) => number;

/**
 * Binary search over a sorted array.
 *
 * @param array the array to search
 * @param searchValue the value to look for
 * @param comparator optional function returning positive if the element is greater
 *   than the search value, negative if lower, or 0 when equal. Defaults to numeric
 *   comparison.
 * @returns the index of a matching element, or the bitwise complement (`~insertionPoint`)
 *   of the index where it would be inserted when no exact match exists. Callers in the
 *   replay tool take `Math.abs(...)` of the result to get the nearest index.
 */
export function binaryIndexOf<T, V>(
    array: T[],
    searchValue: V,
    comparator?: Comparator<T, V>
): number {
    let minIndex = 0;
    let maxIndex = array.length - 1;
    let currentIndex: number;
    let currentElement: T;

    const compare: Comparator<T, V> =
        comparator ||
        ((element, value) =>
            (element as unknown as number) - (value as unknown as number));

    while (minIndex <= maxIndex) {
        currentIndex = ((minIndex + maxIndex) / 2) | 0;
        currentElement = array[currentIndex];

        const diff = compare(currentElement, searchValue);

        if (diff < 0) {
            minIndex = currentIndex + 1;
        } else if (diff > 0) {
            maxIndex = currentIndex - 1;
        } else {
            return currentIndex;
        }
    }

    return ~maxIndex;
}

/**
 * Compares the timestamp of an event-like object to a target timestamp.
 */
export function eventComparator(
    event: { timestamp?: number },
    targetTimestamp: number
): number {
    return (event.timestamp || 0) - targetTimestamp;
}

/**
 * Compares the `time` of a fitness point (e.g. heart-rate) to a target timestamp.
 */
export function timeComparator(
    point: { time?: number },
    targetTimestamp: number
): number {
    return (point.time || 0) - targetTimestamp;
}
